import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Building2, Image, Settings, LogOut, Save, Send, MapPin, Users,
  AlertCircle, CheckCircle, Clock, X, Wifi, Car, Music, Shield, Bell, MessageCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { MediaGallery, ImageUrlGallery } from '../../components/MediaGallery';
import { NotificationsPanel } from '../../components/NotificationBell';
import { UserInbox } from '../../components/Inbox';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = ({ activeTab, setActiveTab }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    { id: 'profile', icon: Building2, label: 'Mi Venue' },
    { id: 'media', icon: Image, label: t('dashboard.media') },
    { id: 'messages', icon: MessageCircle, label: 'Mensajes' },
    { id: 'notifications', icon: Bell, label: 'Notificaciones' },
    { id: 'settings', icon: Settings, label: t('dashboard.settings') },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-white/5 flex flex-col z-40">
      <div className="p-6 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="/gs-agency-logo.png" 
            alt="GS Agency" 
            className="h-10 w-auto object-contain"
          />
        </Link>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto">
        <p className="text-white/40 text-xs uppercase tracking-wider px-4 mb-4">Panel de Venue</p>
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                activeTab === item.id
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 px-4 py-3 mb-2">
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
            <Building2 size={16} className="text-white/60" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm truncate">{user?.name}</p>
            <p className="text-white/40 text-xs">Venue Owner</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors text-sm"
        >
          <LogOut size={18} />
          {t('nav.logout')}
        </button>
      </div>
    </aside>
  );
};

const StatusBadge = ({ status }) => {
  const config = {
    draft: { bg: 'bg-white/10', text: 'text-white/60', icon: Clock, label: 'Borrador' },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock, label: 'Pendiente de Aprobación' },
    approved: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle, label: 'Aprobado y Visible' },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', icon: X, label: 'Rechazado' },
    changes_requested: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertCircle, label: 'Cambios Solicitados' },
  };

  const { bg, text, icon: Icon, label } = config[status] || config.draft;

  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${bg} ${text}`}>
      <Icon size={14} />
      {label}
    </span>
  );
};

const ProfileTab = ({ token, user }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: { es: '', en: '' },
    description: { es: '', en: '' },
    technical_info: { es: '', en: '' },
    restrictions: { es: '', en: '' },
    venue_type: [],
    city: '',
    country: '',
    address: '',
    capacity: 0,
    features: [],
    amenities: [],
    event_types: [],
    indoor: true,
    outdoor: false,
    rooftop: false,
    beach: false,
    cover_image: '',
    gallery: [],
    videos: [],
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/venue/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          setProfile(response.data);
          setFormData({
            name: response.data.name || { es: '', en: '' },
            description: response.data.description || { es: '', en: '' },
            technical_info: response.data.technical_info || { es: '', en: '' },
            restrictions: response.data.restrictions || { es: '', en: '' },
            venue_type: response.data.venue_type || [],
            city: response.data.city || '',
            country: response.data.country || '',
            address: response.data.address || '',
            capacity: response.data.capacity || 0,
            features: response.data.features || [],
            amenities: response.data.amenities || [],
            event_types: response.data.event_types || [],
            indoor: response.data.indoor ?? true,
            outdoor: response.data.outdoor ?? false,
            rooftop: response.data.rooftop ?? false,
            beach: response.data.beach ?? false,
            cover_image: response.data.cover_image || '',
            gallery: response.data.gallery || [],
            videos: response.data.videos || [],
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) {
      fetchProfile();
    }
  }, [token]);

  const handleChange = (field, value, subLang = null) => {
    if (subLang) {
      setFormData(prev => ({
        ...prev,
        [field]: { ...prev[field], [subLang]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async (submit = false) => {
    setSaving(true);
    try {
      if (!profile) {
        await axios.post(`${API}/venue/profile`, {
          ...formData,
          slug: `venue-${Date.now()}`
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.put(`${API}/venue/profile`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (submit) {
        await axios.post(`${API}/venue/profile/submit`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Venue enviado para revisión. Te notificaremos cuando sea aprobado.');
      } else {
        toast.success('Venue guardado como borrador');
      }

      const response = await axios.get(`${API}/venue/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      toast.error('Error al guardar');
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const venueTypes = ['Club', 'Rooftop', 'Beach Club', 'Hotel', 'Restaurant', 'Bar', 'Warehouse', 'Arena', 'Garden', 'Terrace'];
  const amenitiesList = ['DJ Booth', 'Sound System', 'Lighting', 'Stage', 'Bar', 'Kitchen', 'Parking', 'Security', 'WiFi', 'AV Equipment', 'Green Room', 'VIP Areas'];

  return (
    <div className="space-y-8">
      {/* Header with status */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-white mb-2">Mi Venue</h2>
          {profile && <StatusBadge status={profile.status} />}
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="btn-secondary flex items-center gap-2 text-sm px-5 py-2.5"
          >
            <Save size={16} />
            {t('dashboard.save_draft')}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || profile?.status === 'pending'}
            className="btn-gold flex items-center gap-2 text-sm px-5 py-2.5"
          >
            <Send size={16} />
            {t('dashboard.submit_review')}
          </button>
        </div>
      </div>

      {/* Review notes alert */}
      {profile?.review_notes?.length > 0 && (profile.status === 'changes_requested' || profile.status === 'rejected') && (
        <div className={`rounded-xl p-4 border ${
          profile.status === 'rejected' ? 'bg-red-500/10 border-red-500/30' : 'bg-orange-500/10 border-orange-500/30'
        }`}>
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className={profile.status === 'rejected' ? 'text-red-400' : 'text-orange-400'} />
            <div>
              <h4 className={`font-medium mb-1 ${profile.status === 'rejected' ? 'text-red-400' : 'text-orange-400'}`}>
                {profile.status === 'rejected' ? 'Venue Rechazado' : 'Cambios Solicitados'}
              </h4>
              <p className="text-white/70 text-sm">
                {profile.review_notes[profile.review_notes.length - 1]?.notes}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Profile visibility info */}
      {profile?.status === 'approved' && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircle size={20} className="text-green-400" />
            <div>
              <p className="text-green-400 font-medium">Tu venue está visible públicamente</p>
              <a 
                href={`/venues/${profile.slug}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-green-400/70 text-sm hover:underline"
              >
                Ver página pública →
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Basic Info */}
        <div className="glass-dark rounded-xl p-6 space-y-6">
          <h3 className="text-white font-medium flex items-center gap-2">
            <Building2 size={18} className="text-[#D4AF37]" />
            Información del Venue
          </h3>
          
          <div>
            <label className="block text-white/60 text-sm mb-2">Nombre del Venue (ES) *</label>
            <Input
              value={formData.name.es}
              onChange={(e) => handleChange('name', e.target.value, 'es')}
              className="input-premium w-full"
              placeholder="Sky Lounge CDMX"
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Venue Name (EN)</label>
            <Input
              value={formData.name.en}
              onChange={(e) => handleChange('name', e.target.value, 'en')}
              className="input-premium w-full"
              placeholder="Sky Lounge CDMX"
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Tipo de Venue</label>
            <div className="flex flex-wrap gap-2">
              {venueTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    const current = formData.venue_type;
                    if (current.includes(type)) {
                      handleChange('venue_type', current.filter(t => t !== type));
                    } else {
                      handleChange('venue_type', [...current, type]);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                    formData.venue_type.includes(type)
                      ? 'bg-[#D4AF37] text-black'
                      : 'bg-white/10 text-white/60 hover:bg-white/20'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Capacidad *</label>
            <Input
              type="number"
              value={formData.capacity}
              onChange={(e) => handleChange('capacity', parseInt(e.target.value) || 0)}
              className="input-premium w-full"
              placeholder="500"
            />
          </div>
        </div>

        {/* Location */}
        <div className="glass-dark rounded-xl p-6 space-y-6">
          <h3 className="text-white font-medium flex items-center gap-2">
            <MapPin size={18} className="text-[#D4AF37]" />
            Ubicación
          </h3>
          
          <div>
            <label className="block text-white/60 text-sm mb-2">Dirección *</label>
            <Input
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              className="input-premium w-full"
              placeholder="Av. Presidente Masaryk 123, Polanco"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-white/60 text-sm mb-2">Ciudad *</label>
              <Input
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className="input-premium w-full"
                placeholder="Ciudad de México"
              />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">País *</label>
              <Input
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className="input-premium w-full"
                placeholder="México"
              />
            </div>
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-4">Tipo de Espacio</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'indoor', label: 'Interior', icon: Building2 },
                { key: 'outdoor', label: 'Exterior', icon: MapPin },
                { key: 'rooftop', label: 'Rooftop', icon: Building2 },
                { key: 'beach', label: 'Playa', icon: MapPin },
              ].map((option) => (
                <label key={option.key} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData[option.key]}
                    onChange={(e) => handleChange(option.key, e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="glass-dark rounded-xl p-6 space-y-6">
          <h3 className="text-white font-medium">Descripción</h3>
          
          <div>
            <label className="block text-white/60 text-sm mb-2">Descripción (ES) *</label>
            <Textarea
              value={formData.description.es}
              onChange={(e) => handleChange('description', e.target.value, 'es')}
              className="input-premium w-full resize-none"
              rows={5}
              placeholder="Describe tu espacio, ambiente y experiencia..."
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Description (EN)</label>
            <Textarea
              value={formData.description.en}
              onChange={(e) => handleChange('description', e.target.value, 'en')}
              className="input-premium w-full resize-none"
              rows={5}
              placeholder="Describe your space, atmosphere and experience..."
            />
          </div>
        </div>

        {/* Amenities */}
        <div className="glass-dark rounded-xl p-6 space-y-6">
          <h3 className="text-white font-medium">Amenidades y Equipamiento</h3>
          
          <div className="flex flex-wrap gap-2">
            {amenitiesList.map((amenity) => (
              <button
                key={amenity}
                type="button"
                onClick={() => {
                  const current = formData.amenities;
                  if (current.includes(amenity)) {
                    handleChange('amenities', current.filter(a => a !== amenity));
                  } else {
                    handleChange('amenities', [...current, amenity]);
                  }
                }}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                  formData.amenities.includes(amenity)
                    ? 'bg-[#C0C0C0] text-black'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                {amenity}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Características adicionales (separadas por coma)</label>
            <Input
              value={formData.features.join(', ')}
              onChange={(e) => handleChange('features', e.target.value.split(',').map(f => f.trim()).filter(Boolean))}
              className="input-premium w-full"
              placeholder="Vista panorámica, Área VIP, Terraza cubierta"
            />
          </div>
        </div>

        {/* Technical Info */}
        <div className="glass-dark rounded-xl p-6 space-y-6">
          <h3 className="text-white font-medium">Información Técnica</h3>
          
          <div>
            <label className="block text-white/60 text-sm mb-2">Especificaciones Técnicas (ES)</label>
            <Textarea
              value={formData.technical_info.es}
              onChange={(e) => handleChange('technical_info', e.target.value, 'es')}
              className="input-premium w-full resize-none"
              rows={4}
              placeholder="Sistema de sonido, iluminación, dimensiones del escenario..."
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Restricciones (ES)</label>
            <Textarea
              value={formData.restrictions.es}
              onChange={(e) => handleChange('restrictions', e.target.value, 'es')}
              className="input-premium w-full resize-none"
              rows={3}
              placeholder="Horarios, límites de ruido, requisitos especiales..."
            />
          </div>
        </div>

        {/* Videos */}
        <div className="glass-dark rounded-xl p-6 space-y-6">
          <h3 className="text-white font-medium">Videos</h3>
          
          <div>
            <label className="block text-white/60 text-sm mb-2">URLs de Videos (separadas por coma)</label>
            <Input
              value={formData.videos.join(', ')}
              onChange={(e) => handleChange('videos', e.target.value.split(',').map(v => v.trim()).filter(Boolean))}
              className="input-premium w-full"
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        </div>

        {/* Cover Image & Gallery */}
        <div className="glass-dark rounded-xl p-6 space-y-6 lg:col-span-2">
          <h3 className="text-white font-medium flex items-center gap-2">
            <Image size={18} className="text-[#D4AF37]" />
            Imágenes
          </h3>

          <div>
            <label className="block text-white/60 text-sm mb-2">Imagen de Portada (URL) *</label>
            <Input
              value={formData.cover_image}
              onChange={(e) => handleChange('cover_image', e.target.value)}
              className="input-premium w-full"
              placeholder="https://..."
            />
            {formData.cover_image && (
              <img src={formData.cover_image} alt="Cover preview" className="mt-3 h-40 object-cover rounded-lg" />
            )}
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Galería de Fotos</label>
            <ImageUrlGallery
              urls={formData.gallery}
              onUrlsChange={(urls) => handleChange('gallery', urls)}
              maxItems={20}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const MediaTab = ({ token }) => {
  const [mediaItems, setMediaItems] = useState([]);

  return (
    <div className="space-y-8">
      <h2 className="font-display text-2xl text-white">Mis Archivos</h2>

      <div className="glass-dark rounded-xl p-8">
        <MediaGallery
          items={mediaItems}
          onItemsChange={setMediaItems}
          token={token}
          folder="venues"
          maxItems={30}
        />
      </div>

      <div className="glass-dark rounded-xl p-6">
        <h3 className="text-white font-medium mb-4">Consejos para tus fotos</h3>
        <ul className="space-y-2 text-white/60 text-sm">
          <li>• Incluye fotos del espacio vacío y con eventos</li>
          <li>• Muestra diferentes áreas: entrada, pista, bar, VIP, etc.</li>
          <li>• Fotos de día y de noche para mostrar versatilidad</li>
          <li>• Imágenes de alta resolución (mínimo 1920x1080px)</li>
          <li>• Formatos aceptados: JPG, PNG, WEBP (máx 50MB)</li>
        </ul>
      </div>
    </div>
  );
};

export default function VenueDashboard() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  const renderTab = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab token={token} user={user} />;
      case 'media':
        return <MediaTab token={token} />;
      case 'messages':
        return <UserInbox />;
      case 'notifications':
        return <NotificationsPanel />;
      case 'settings':
        return (
          <div className="space-y-8">
            <h2 className="font-display text-2xl text-white">Configuración</h2>
            <div className="glass-dark rounded-xl p-6">
              <p className="text-white/60">Para cambiar tu información de cuenta, contacta a support@gsagency.com</p>
            </div>
          </div>
        );
      default:
        return <ProfileTab token={token} user={user} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="venue-dashboard">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <main className="ml-64 p-8">
        <div className="max-w-5xl mx-auto">
          {renderTab()}
        </div>
      </main>
    </div>
  );
}
